/**
 * Teams API Service
 * Provides API integration for team operations
 * Handles all CRUD operations for agent teams
 */

import { api } from "../utils/api-client";
import type {
  Team,
  TeamQueryParams,
  TeamPagination,
  TeamAgent,
  TeamAccessModes,
  TeamMemoryConfig,
} from "../types/team-types";

/**
 * API response structure for team list endpoint
 * Contains team data with pagination metadata
 */
interface TeamsListResponse {
  /**
   * Success indicator
   * True if request completed successfully
   */
  success: boolean;

  /**
   * Array of team records
   * Contains teams matching query params
   */
  data: Team[];

  /**
   * Pagination metadata
   * Information for navigating pages
   */
  pagination: TeamPagination;
}

/**
 * API response structure for single team endpoint
 * Contains team data with embedded agents
 */
interface TeamResponse {
  /**
   * Success indicator
   * True if request completed successfully
   */
  success: boolean;

  /**
   * Team record with agents
   * Full team data including agent relationships
   */
  data: Team & { agents: TeamAgent[] };
}

/**
 * Input data for creating a new team
 * Contains all required fields for team creation
 */
export interface CreateTeamInput {
  /**
   * Team display name
   * Required, max 255 characters
   */
  name: string;

  /**
   * Team description
   * Optional explanation of team purpose
   */
  description?: string;

  /**
   * Team icon emoji
   * Optional visual identifier
   */
  icon?: string;

  /**
   * System prompt for the team
   * Additional instructions applied to all agents.
   */
  systemPrompt?: string;

  /**
   * Collaboration pattern type
   * Determines how agents work together
   */
  pattern: Team["pattern"];

  /**
   * Pattern-specific configuration
   * Settings for the collaboration pattern
   */
  patternConfig: Record<string, unknown>;

  /**
   * Agent assignments
   * List of agents with their roles
   */
  agents: Array<{
    /**
     * Agent identifier
     * Reference to existing agent
     */
    agentId: string;

    /**
     * Agent role in team
     * Based on collaboration pattern
     */
    role: TeamAgent["role"];

    /**
     * Execution order
     * For pipeline pattern sequencing
     */
    order?: number;

    /**
     * Custom instructions
     * Override agent's default instructions
     */
    instructions?: string;

    /**
     * Debate perspective
     * For debate pattern viewpoint
     */
    perspective?: string;

    /**
     * Agent skills
     * For hierarchical delegation
     */
    skills?: string[];

    /**
     * Agent weight
     * For voting and merge operations
     */
    weight?: number;
  }>;
}

/**
 * Input data for updating a team
 * All fields are optional for partial updates
 */
export interface UpdateTeamInput {
  /**
   * New team name
   * Optional, max 255 characters
   */
  name?: string;

  /**
   * New description
   * Optional explanation of team purpose
   */
  description?: string;

  /**
   * New icon emoji
   * Optional visual identifier
   */
  icon?: string;

  /**
   * System prompt for all agents
   * Global instructions for team behavior
   */
  systemPrompt?: string;

  /**
   * Pattern execution instructions
   * Guidance for collaboration
   */
  patternInstructions?: string;

  /**
   * New status
   * Changes team availability
   */
  status?: Team["status"];

  /**
   * Published status
   * Whether team is publicly accessible
   */
  published?: boolean;

  /**
   * Domain restriction enabled
   * Only allow access from specific domains
   */
  domainRestrictionEnabled?: boolean;

  /**
   * Allowed domains for access
   * List of domains that can access the team
   */
  allowedDomains?: string[];

  /**
   * Embed configuration
   * Settings for embedded team chat
   */
  embedConfig?: {
    enabled: boolean;
    color: string;
    showBranding: boolean;
    bubbleMode: boolean;
    welcomeMessage: string;
  };

  /**
   * Updated pattern config
   * Changes pattern settings
   */
  patternConfig?: Record<string, unknown>;

  /**
   * Access mode settings
   * Controls interaction methods
   */
  accessModes?: TeamAccessModes;

  /**
   * Memory configuration
   * Controls history and learning
   */
  memoryConfig?: TeamMemoryConfig;

  /**
   * Cross-chat context persistence settings
   * When persistContext is true, form data and progress are injected into every chat
   */
  teamContext?: {
    persistContext?: boolean;
    context?: string | null;
  };

  /**
   * Updated agent assignments
   * Replaces current agent list
   */
  agents?: CreateTeamInput["agents"];
}

/**
 * Input data for updating team agents
 */
export interface UpdateTeamAgentsInput {
  /**
   * Agent assignments
   * List of agents with their roles
   */
  agents: Array<{
    /**
     * Agent identifier
     * Reference to existing agent
     */
    agentId: string;

    /**
     * Agent role in team
     * Based on collaboration pattern
     */
    role: TeamAgent["role"];

    /**
     * Execution order
     * For pipeline pattern sequencing
     */
    order?: number;

    /**
     * Custom instructions
     * Override agent's default instructions
     */
    instructions?: string;

    /**
     * Debate perspective
     * For debate pattern viewpoint
     */
    perspective?: string;

    /**
     * Agent skills
     * For hierarchical delegation
     */
    skills?: string[];

    /**
     * Agent weight
     * For voting and merge operations
     */
    weight?: number;
  }>;
}

/**
 * Fetches teams with optional filtering and pagination
 * Retrieves team list based on query parameters
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Promise with teams array and pagination metadata
 */
export async function fetchTeams(
  params: TeamQueryParams = {}
): Promise<{ teams: Team[]; pagination: TeamPagination }> {
  /**
   * Build URL search params from query parameters
   * Filters out undefined values to avoid empty params
   */
  const searchParams = new URLSearchParams();

  if (params.pattern) searchParams.set("pattern", params.pattern);
  if (params.status) searchParams.set("status", params.status);
  if (params.search) searchParams.set("search", params.search);
  if (params.limit) searchParams.set("limit", String(params.limit));
  if (params.offset) searchParams.set("offset", String(params.offset));

  const queryString = searchParams.toString();
  const url = queryString ? `/teams?${queryString}` : "/teams";

  const response = (await api.get(url)) as TeamsListResponse;

  return {
    teams: response.data,
    pagination: response.pagination,
  };
}

/**
 * Fetches teams including each team's most recent chat ID
 * Uses the teams listing endpoint enriched by backend response shaping
 *
 * @param params - Query parameters for filtering and pagination
 * @returns Promise with teams array and pagination metadata
 */
export async function fetchTeamsWithRecentChat(
  params: TeamQueryParams = {}
): Promise<{ teams: Team[]; pagination: TeamPagination }> {
  return fetchTeams(params);
}

/**
 * Fetches a single team by ID with its agents
 * Retrieves full team data including agent relationships
 *
 * @param id - Team identifier to fetch
 * @returns Promise with team data and embedded agents
 */
export async function fetchTeamById(
  id: string
): Promise<Team & { agents: TeamAgent[] }> {
  const response = (await api.get(`/teams/${id}`)) as TeamResponse;
  return response.data;
}

/**
 * Creates a new team with agents
 * Validates input and creates team with agent assignments
 *
 * @param input - Team creation data with agents
 * @returns Promise with created team and agents
 */
export async function createTeam(
  input: CreateTeamInput
): Promise<Team & { agents: TeamAgent[] }> {
  const response = (await api.post("/teams", input)) as TeamResponse;
  return response.data;
}

/**
 * Updates an existing team
 * Supports partial updates for any team fields
 *
 * @param id - Team identifier to update
 * @param input - Update data with changed fields
 * @returns Promise with updated team and agents
 */
export async function updateTeam(
  id: string,
  input: UpdateTeamInput
): Promise<TeamResponse> {
  const response = (await api.put(`/teams/${id}`, input)) as TeamResponse;
  return response;
}

/**
 * Deletes a team permanently
 * Removes team and all associated data
 *
 * @param id - Team identifier to delete
 * @returns Promise that resolves when deleted
 */
export async function deleteTeam(id: string): Promise<void> {
  await api.delete(`/teams/${id}`);
}

/**
 * Updates team agents
 * Replaces all agent assignments for a team
 *
 * @param id - Team identifier to update
 * @param input - Agent assignments data
 * @returns Promise with updated team and agents
 */
export async function updateTeamAgents(
  id: string,
  input: UpdateTeamAgentsInput
): Promise<Team & { agents: TeamAgent[] }> {
  const response = (await api.put(`/teams/${id}/agents`, input)) as TeamResponse;
  return response.data;
}

/**
 * Enhances a prompt using AI
 * Improves system instructions for better results
 *
 * @param input - Enhancement request data
 * @returns Promise with enhanced prompt and metadata
 */
export async function enhancePrompt(input: {
  text: string;
}): Promise<{
  success: boolean;
  data: {
    enhancedPrompt: string;
    enhancementMetadata: {
      originalLength: number;
      enhancedLength: number;
    };
  };
  message?: string;
}> {
  return await api.post("/ai/enhance-prompt", input);
}

/**
 * Generates a team description using AI
 * Creates description based on team name and instructions
 *
 * @param input - Description generation data
 * @returns Promise with generated description
 */
export async function generateTeamDescription(input: {
  teamName: string;
  currentDescription?: string;
  systemPrompt?: string;
}): Promise<{
  success: boolean;
  data: {
    description: string;
  };
  message?: string;
}> {
  return await api.post("/teams/generate-description", input);
}

/**
 * Saves intake form data to a team (once per workspace)
 * Persists the filled form so subsequent chats skip the form step
 *
 * @param teamId - Team identifier
 * @param formData - Form values filled by the user
 */
export async function saveTeamIntakeFormData(
  teamId: string,
  formData: Record<string, unknown>
): Promise<void> {
  await api.patch(`/teams/${teamId}/intake-form`, { formData });
}

/**
 * Gets intake form data for a team (if previously filled)
 * Returns null when no form data has been saved yet
 *
 * @param teamId - Team identifier
 * @returns Promise with form data or null
 */
export async function getTeamIntakeFormData(
  teamId: string
): Promise<Record<string, unknown> | null> {
  const response = await api.get(`/teams/${teamId}/intake-form`);
  return response?.data?.intakeFormData ?? null;
}

/**
 * Teams service object for dependency injection
 * Provides all team API operations in a single object
 */
export const teamsService = {
  fetchTeams,
  fetchTeamsWithRecentChat,
  fetchTeamById,
  createTeam,
  updateTeam,
  deleteTeam,
  updateTeamAgents,
  enhancePrompt,
  generateTeamDescription,
};

// ============================================================================
// Team Chat API Methods
// ============================================================================

/**
 * Chat history item for team chats
 * Similar to agent chat history items
 */
export interface TeamChatHistoryItem {
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messageCount?: number;
  lastMessage?: {
    preview: string;
    createdAt: string;
  };
}

/**
 * Response structure for team chat history endpoint
 * Groups chats by time period like agent chats
 */
export interface TeamChatHistoryResponse {
  success: boolean;
  data: {
    today?: TeamChatHistoryItem[];
    yesterday?: TeamChatHistoryItem[];
    lastWeek?: TeamChatHistoryItem[];
    lastMonth?: TeamChatHistoryItem[];
    older?: TeamChatHistoryItem[];
  };
  pagination?: {
    total: number;
    page: number;
    limit: number;
    hasNextPage: boolean;
  };
}

/**
 * Creates a new chat for a team
 *
 * @param teamId - Team ID to create chat for
 * @param data - Chat creation data
 * @returns Promise with created chat record
 */
export async function createTeamChat(
  teamId: string,
  data: {
    title?: string;
    userId?: string;
    workspaceId?: string;
  } = {}
): Promise<{
  id: string;
  title: string;
  createdAt: string;
}> {
  const response = await api.post(`/teams/${teamId}/chats`, data);
  return response.data;
}

/**
 * Gets chat history for a team
 * Chats are grouped by time periods for better UX
 *
 * @param teamId - Team ID to fetch chats for
 * @param params - Query parameters for pagination
 * @returns Promise with grouped chat history
 */
export async function getTeamChatHistory(
  teamId: string,
  params?: {
    page?: number;
    limit?: number;
  }
): Promise<TeamChatHistoryResponse> {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set("page", String(params.page));
  if (params?.limit) searchParams.set("limit", String(params.limit));

  const queryString = searchParams.toString();
  const url = queryString
    ? `/teams/${teamId}/chats?${queryString}`
    : `/teams/${teamId}/chats`;

  return await api.get(url) as TeamChatHistoryResponse;
}

/**
 * Gets a specific team chat by ID
 *
 * @param teamId - Team ID
 * @param chatId - Chat ID
 * @returns Promise with chat data
 */
export async function getTeamChatById(
  teamId: string,
  chatId: string
): Promise<{
  id: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  teamId: string;
}> {
  const response = await api.get(`/teams/${teamId}/chats/${chatId}`);
  return response.data;
}

/**
 * Updates a team chat title
 *
 * @param teamId - Team ID
 * @param chatId - Chat ID
 * @param title - New title
 * @returns Promise that resolves when updated
 */
export async function updateTeamChatTitle(
  teamId: string,
  chatId: string,
  title: string
): Promise<void> {
  await api.patch(`/teams/${teamId}/chats/${chatId}`, { title });
}

/**
 * Deletes a team chat
 *
 * @param teamId - Team ID
 * @param chatId - Chat ID to delete
 * @returns Promise that resolves when deleted
 */
export async function deleteTeamChat(
  teamId: string,
  chatId: string
): Promise<void> {
  await api.delete(`/teams/${teamId}/chats/${chatId}`);
}

/**
 * Gets public team info for channel users
 * Used on login/register pages to show team info
 *
 * @param teamId - Team ID
 * @returns Promise with public team data
 */
export async function getPublicTeamInfo(
  teamId: string
): Promise<{
  id: string;
  name: string;
  description?: string;
  icon?: string;
  published: boolean;
}> {
  const response = await api.get(`/teams/${teamId}/public`, {
    requiresAuth: false,
    showErrorToast: false,
  });
  return response.data;
}

/**
 * Team chat service object
 * Provides all team chat API operations
 */
export const teamChatService = {
  createTeamChat,
  getTeamChatHistory,
  getTeamChatById,
  updateTeamChatTitle,
  deleteTeamChat,
  getPublicTeamInfo,
};

// ============================================================================
// Team Dashboard API Methods
// ============================================================================

/**
 * Response structure for hydrated dashboard endpoint
 */
export interface HydratedDashboardResponse {
  id: string;
  name: string;
  description?: string;
  icon?: string;
  spec: { root: string; elements: Record<string, unknown> };
  collections: Record<string, number>;
}

/**
 * Fetches a hydrated dashboard spec with live data from team records.
 * Resolves data bindings like `{{collection | operation}}` server-side.
 *
 * @param teamId - Team ID
 * @param dashboardId - Dashboard spec ID
 * @returns Promise with hydrated dashboard spec and collection summary
 */
export async function getHydratedDashboard(
  teamId: string,
  dashboardId: string
): Promise<HydratedDashboardResponse> {
  const response = await api.get(
    `/teams/${teamId}/dashboards/${dashboardId}/hydrated`
  );
  return response.data;
}
